// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DropdownOption, RouteParamsWithLanguage, StoryZoneProps } from "../../../../../../types/screensTypes";
import { saveStory, getStoryById, getZonesAndRooms } from "../../../../../../api/museum";
import Dropdown from "../../../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../../../context/TrackCreationContext";
import Button from "../../../../../../components/ui/Button";
import stepStyles from "../../../createStep.module.css";

const StoryZone: React.FC<StoryZoneProps> = ({ navigateToNext }) => {
    const { storyId, planId, language } = useParams<RouteParamsWithLanguage>(); // Get parameters from URL
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { trackData } = useTrackCreation();

    // State for dropdown options and selections
    const [zones, setZones] = useState<Record<string, DropdownOption[]>>({});
    const [zonesDropDown, setZonesDropDown] = useState<DropdownOption[]>([]);
    const [roomsDropDown, setRoomsDropDown] = useState<DropdownOption[]>([]);
    const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
    const [selectedZone, setSelectedZone] = useState<string | null>(null);
    const [museumHasAnyRoom, setMuseumHasAnyRoom] = useState(false);

    // Save the story with the selected room and zone
    const saveStoryAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !storyId || !language || !selectedRoom) {
            return;
        }

        if (museumHasAnyRoom && !selectedZone) {
            return;
        }

        const tempWpt = await saveStory({
            token,
            trackId,
            clickableZoneId: storyId,
            subplanId: selectedRoom,
            zoneId: selectedZone || undefined,
            language,
        });

        if (tempWpt && tempWpt.clickableZoneId && planId) {
            const id: string = tempWpt.clickableZoneId;
            navigateToNext(id, language, planId);
        }
    };

    // Fetch story info to preselect room and zone
    const getStoryAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !storyId || !language) {
            return;
        }
        const storyData = await getStoryById({
            token,
            trackId,
            clickableZoneId: storyId,
            language,
        });
        if (storyData) {
            const zoneId: string = storyData.subPlansZonesId;
            setSelectedRoom(storyData.subPlansId);
            setSelectedZone(zoneId);
        }
    };

    // Fetch zones and rooms for the track
    const getZoneOptions = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) {
            return;
        }
        const zonesAndRooms = await getZonesAndRooms({ token, trackId });
        const data = zonesAndRooms;
        if (data && data.subPlans && data.subPlans.length > 0) {
            const roomsList = data.subPlans;
            const roomsForDropdown: DropdownOption[] = roomsList
                .sort((a: any, b: any) => a.floorNumber - b.floorNumber)
                .map((room: any) => ({
                    label: room.internalName?.trim() || `${t("floorNumber")} ${room.floorNumber}`,
                    value: room.id,
                }));
            setRoomsDropDown(roomsForDropdown);

            const allZones = data.subPlansZones || [];
            setMuseumHasAnyRoom(allZones.length > 0);

            const zonesByRoom: Record<string, DropdownOption[]> = roomsList.reduce(
                (acc: Record<string, DropdownOption[]>, room: any) => {
                    acc[room.id] = allZones
                        .filter((zone: any) => zone.subPlansId === room.id)
                        .map((zone: any) => ({
                            // Initial mapping: label holds zone.id and value holds zone.internalName
                            label: zone.id,
                            value: zone.internalName?.trim() || `Zone ${zone.position ?? ""}`.trim(),
                        }));
                    return acc;
                },
                {},
            );
            setZones(zonesByRoom);
        }
    };

    // When storyId changes, fetch its details
    useEffect(() => {
        if (storyId && storyId !== "" && trackData.trackId) {
            getStoryAPI();
        }
    }, [storyId, language, trackData.trackId]);

    // Fetch room and zone options once the track is available
    useEffect(() => {
        if (trackData && trackData.trackId) {
            getZoneOptions();
        }
    }, [trackData.trackId]);

    // Update dropdown options for zones when a room is selected
    useEffect(() => {
        if (zones && selectedRoom) {
            const zonesForDropdown: DropdownOption[] =
                zones[selectedRoom]?.map((zone) => ({
                    // Swap the keys so that the dropdown displays the zone's internalName
                    label: zone.value,
                    value: zone.label,
                })) || [];
            setZonesDropDown(zonesForDropdown);
        }
    }, [zones, selectedRoom]);

    return (
        <div>
            <TitleSubtitle title={t("storyCategory")} subtitle={""} layout="step" />

            <div>
                <h5>{t("pleaseSelectAPlan")}</h5>
                <Dropdown
                    label=""
                    options={roomsDropDown}
                    onChange={(value: string) => setSelectedRoom(value)}
                    value={selectedRoom || ""}
                    placeholder={t("pleaseSelectAPlan")}
                    loading={false}
                    error={false}
                />
            </div>
            <div>
                <h5>{t("pleaseSelectARoom")}</h5>
                <Dropdown
                    label=""
                    options={zonesDropDown}
                    onChange={(value: string) => setSelectedZone(value)}
                    value={selectedZone || ""}
                    placeholder={t("pleaseSelectARoom")}
                    loading={false}
                    error={false}
                />
                {museumHasAnyRoom && !selectedZone && (
                    <p style={{ color: "var(--warn, #c47a00)", fontSize: 14, marginTop: 10 }} role="status">
                        {zonesDropDown.length > 0
                            ? t("pleaseSelectARoomBeforeAddingStory")
                            : t("createRoomOnFloorBeforeAddingStory")}
                    </p>
                )}
            </div>
            <div className={stepStyles.stepButton}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    disabled={museumHasAnyRoom && !selectedZone}
                    onClick={async () => {
                        await saveStoryAPI();
                    }}
                />
            </div>
        </div>
    );
};

export default StoryZone;
